import { PostDTO } from '../../models/post'
import AvatarEditor from 'react-avatar-editor'
import { dateTimeFormatter, getDate, isAdmin, isCurrentUser } from '../../helpers/utils'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import CommentIcon from '@mui/icons-material/Comment'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import { IconButton } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import MenuDropdown, { MenuOption } from '../../components/MenuDropdown'
import PostService from '../../services/PostService'
import { useSnackbar } from 'notistack'
import CustomModal from '../../containers/Modal'
import PostModal from '../../components/Modal/PostModal'
import { Dispatch, SetStateAction, useState } from 'react'
import TextModal from '../../components/Modal/TextModal'
import CommentModal from '../../components/Modal/CommentModal'
import { LikesTooltip } from '../../components/Tooltip'
import { Tooltip } from 'react-tooltip'
import LikesModal from '../../components/Modal/LikesModal'
import { FetchResponse } from '../../models/shared'
import ClearIcon from '@mui/icons-material/Clear'
import UserListModal from '../../components/Modal/UserListModal'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import SinglePostComments from '../posts/SinglePostComments'
import VisibilityIcon from '@mui/icons-material/Visibility'

interface Props {
    info: PostDTO
    setMutate?: Dispatch<SetStateAction<boolean>>
    handleClose?: () => void
    single?: boolean
    canDelete?: boolean
}

type ActionPost = 'APPROVE' | 'REJECT' | 'DELETE'

const Post = ({
    info,
    setMutate = () => {},
    handleClose,
    single = false,
    canDelete = false,
}: Props) => {
    const { enqueueSnackbar } = useSnackbar()
    const [isNewPostOpen, setIsNewPostOpen] = useState<boolean>(false)
    const [isDeletePostOpen, setIsDeletePostOpen] = useState<boolean>(false)
    const [isCommentPostOpen, setIsCommentPostOpen] = useState<boolean>(false)
    const [isAttendeesPostOpen, setIsAttendeesPostOpen] = useState<boolean>(false)
    const [isLikesPostOpen, setIsLikesPostOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()
    const location = useLocation()

    const isLikedByCurrentUser = isAdmin()
        ? false
        : info.likes.some((like) => isCurrentUser(like.user))

    const isCurrentUserAttending =
        isAdmin() || !info.event ? false : info.attendees.some((user) => isCurrentUser(user))

    const handleActionPost = async (action: ActionPost, object?: {}) => {
        let response
        let message

        switch (action) {
            case 'APPROVE':
                response = await new PostService().approvePost(info.id)
                message = 'Publicación aprobada'
                break
            case 'REJECT':
                response = await new PostService().rejectPost(
                    info.id,
                    object as { rejectionReason: '' },
                )
                message = 'Publicación rechazada'
                break
            case 'DELETE':
                response = await new PostService().deletePost(info.id)
                message = 'Publicación eliminada'
                break
            default:
                break
        }

        if ((response as FetchResponse).status === 200) {
            enqueueSnackbar(message, {
                variant: 'success',
                autoHideDuration: 6000,
            })
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    const options: MenuOption[] = [
        {
            title: 'Editar',
            icon: (
                <IconButton className="pointer-events-none">
                    <EditIcon />
                </IconButton>
            ),
            action: () => setIsNewPostOpen(true),
        },
        {
            title: 'Eliminar',
            icon: (
                <IconButton className="pointer-events-none">
                    <DeleteIcon />
                </IconButton>
            ),
            action: () => setIsDeletePostOpen(true),
        },
    ]

    const onlyDeleteOption: MenuOption[] = [
        {
            title: 'Eliminar',
            icon: (
                <IconButton className="pointer-events-none">
                    <DeleteIcon />
                </IconButton>
            ),
            action: () => setIsDeletePostOpen(true),
        },
    ]

    const handleLike = async (like: boolean) => {
        let response

        if (like) {
            response = await new PostService().likePost(info.id)
        } else {
            response = await new PostService().dislikePost(info.id)
        }

        if (response.status === 200) {
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    const handleAttendEvent = async (attending: boolean) => {
        let response

        if (attending) {
            response = await new PostService().joinEvent(info.id)
        } else {
            response = await new PostService().leaveEvent(info.id)
        }

        if (response.status === 200) {
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    const getReversedComments = () => {
        const result = [...info.comments]
        result.reverse()
        return result
    }

    return (
        <>
            <div className="flex flex-col gap-2 md:gap-4">
                {handleClose && (
                    <div className="flex gap-2 md:gap-4 justify-between items-center relative">
                        <label className="text-secondary font-semibold">{'Detalle'}</label>
                        <div className="absolute right-0 top-0">
                            <IconButton
                                className="text-primary"
                                onClick={handleClose}
                            >
                                <ClearIcon />
                            </IconButton>
                        </div>
                    </div>
                )}
                <div className="flex gap-2 md:gap-4 justify-between">
                    <div className="flex gap-2 md:gap-4 items-center">
                        <div
                            onClick={() =>
                                !info.createdByAdmin &&
                                navigate(AppRoutes.user.replace(':userId', info.author.id))
                            }
                            className={!info.createdByAdmin ? 'cursor-pointer' : ''}
                        >
                            <AvatarEditor
                                image={
                                    info.createdByAdmin || !info.author.picture
                                        ? '/adminPhoto.png'
                                        : info.author.picture
                                }
                                height={40}
                                width={40}
                                border={0}
                                className="z-1 rounded-full pointer-events-none"
                            />
                        </div>
                        {info.createdByAdmin ? (
                            <span
                                onClick={() =>
                                    navigate(AppRoutes.singlePost.replace(':postId', info.id), {
                                        state: { goBackPath: location?.pathname },
                                    })
                                }
                                className={'text-xs md:text-sm cursor-pointer hover:underline'}
                            >
                                {getDate(info.dateCreated)}
                            </span>
                        ) : (
                            <div className="flex flex-col">
                                <span
                                    onClick={() =>
                                        navigate(AppRoutes.user.replace(':userId', info.author.id))
                                    }
                                    className={`text-sm md:text-base cursor-pointer hover:underline`}
                                >
                                    {info.author.firstName + ' ' + info.author.lastName}
                                </span>
                                <span
                                    onClick={() =>
                                        navigate(AppRoutes.singlePost.replace(':postId', info.id), {
                                            state: { goBackPath: location?.pathname },
                                        })
                                    }
                                    className="text-[10px] md:text-xs cursor-pointer hover:underline"
                                >
                                    {getDate(info.dateCreated)}
                                </span>
                            </div>
                        )}
                    </div>
                    {((isCurrentUser(info.author) && !isAdmin()) ||
                        (isAdmin() && info.createdByAdmin)) && <MenuDropdown options={options} />}
                    {!isCurrentUser(info.author) && !isAdmin() && canDelete && (
                        <MenuDropdown options={onlyDeleteOption} />
                    )}
                </div>
                <span className={`block break-words max-w-full text-xl md:text-2xl`}>
                    {info.title}
                </span>
                <span
                    className={`block break-words max-w-full whitespace-pre-line text-sm md:text-base ${!single ? 'max-h-[20vh] md:max-h-[36vh] overflow-auto pr-2' : ''}`}
                >
                    {info.content}
                </span>
                {info.benefit && !!info.link && (
                    <div className="flex gap-2">
                        <span className="font-semibold text-sm md:text-base">
                            Enlace al beneficio:{' '}
                        </span>
                        <a
                            href={`${info.link.startsWith('http') ? '' : '///'}${info.link}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary text-sm md:text-base cursor-pointer underline"
                        >
                            Click acá
                        </a>
                    </div>
                )}
                {info.event && (
                    <div className="flex gap-2">
                        <span className="font-semibold text-sm md:text-base">Fecha y hora:</span>
                        <span className="text-sm md:text-base">
                            {dateTimeFormatter(new Date(info.dayOfTheEvent))}
                        </span>
                    </div>
                )}
                {info.image && (
                    <div className="flex">
                        <img
                            alt="Post"
                            src={info.image}
                            className="overflow-y-auto max-h-[250px] md:max-h-[400px]"
                        />
                    </div>
                )}
                {(!isAdmin() || info.createdByAdmin) && (
                    <div className="flex gap-2 md:gap-4">
                        {!single && (
                            <IconButton
                                onClick={() =>
                                    navigate(AppRoutes.singlePost.replace(':postId', info.id), {
                                        state: { goBackPath: location?.pathname },
                                    })
                                }
                                title="Ver más"
                            >
                                <VisibilityIcon />
                            </IconButton>
                        )}
                        <div className="flex items-center">
                            <IconButton
                                className={isLikedByCurrentUser ? 'text-primary' : ''}
                                onClick={() => handleLike(!isLikedByCurrentUser)}
                                disabled={isAdmin()}
                            >
                                <ThumbUpAltIcon />
                            </IconButton>
                            <span
                                className={
                                    info.likes.length > 0
                                        ? `tooltip-${info.id} hover:underline cursor-pointer`
                                        : ''
                                }
                                onClick={() => {
                                    info.likes.length > 0 && setIsLikesPostOpen(true)
                                }}
                            >
                                {info.likes.length}
                                {'  '}
                                <span className="hidden md:inline">Me gusta</span>
                            </span>
                        </div>
                        <div className={`flex items-center ${single ? 'pointer-events-none' : ''}`}>
                            <IconButton
                                onClick={() => setIsCommentPostOpen(true)}
                                disabled={isAdmin() && info.comments.length === 0}
                            >
                                <CommentIcon />
                            </IconButton>
                            <span
                                onClick={() =>
                                    (!isAdmin() || info.comments.length > 0) &&
                                    setIsCommentPostOpen(true)
                                }
                                className={
                                    !isAdmin() || info.comments.length > 0
                                        ? 'hover:underline cursor-pointer'
                                        : ''
                                }
                            >
                                {info.comments.length}{' '}
                                <span className="hidden md:inline">Comentarios</span>
                            </span>
                        </div>
                        {info.event && (
                            <div className="flex items-center">
                                <IconButton
                                    className={isCurrentUserAttending ? 'text-primary' : ''}
                                    onClick={() => handleAttendEvent(!isCurrentUserAttending)}
                                    disabled={isAdmin()}
                                >
                                    <EmojiPeopleIcon />
                                </IconButton>
                                <span
                                    onClick={() =>
                                        info.attendees.length > 0 && setIsAttendeesPostOpen(true)
                                    }
                                    className={
                                        info.attendees.length > 0
                                            ? 'hover:underline cursor-pointer'
                                            : ''
                                    }
                                >
                                    {info.attendees.length}{' '}
                                    <span className="hidden md:inline">Asistentes</span>
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {single && (
                    <SinglePostComments
                        comments={info.comments}
                        postId={info.id}
                        setMutate={setMutate}
                    />
                )}
            </div>
            <CustomModal
                isOpen={isNewPostOpen}
                handleClose={() => setIsNewPostOpen(false)}
            >
                <PostModal
                    info={info}
                    setMutate={setMutate}
                    handleClose={() => setIsNewPostOpen(false)}
                    admin={info.createdByAdmin}
                    isBenefit={info.benefit}
                />
            </CustomModal>
            <CustomModal
                isOpen={isDeletePostOpen}
                handleClose={() => setIsDeletePostOpen(false)}
            >
                <TextModal
                    title={'Eliminar Publicación'}
                    content={'¿Estás seguro de que querés eliminar esta publicación?'}
                    onAction={async () => {
                        setLoading(true)
                        await handleActionPost('DELETE')
                        setLoading(false)
                    }}
                    onClose={() => setIsDeletePostOpen(false)}
                    loading={loading}
                />
            </CustomModal>
            <CustomModal
                isOpen={isCommentPostOpen}
                handleClose={() => setIsCommentPostOpen(false)}
            >
                <CommentModal
                    comments={getReversedComments()}
                    postId={info.id}
                    handleClose={() => setIsCommentPostOpen(false)}
                    setMutate={setMutate}
                />
            </CustomModal>
            <CustomModal
                isOpen={isLikesPostOpen}
                handleClose={() => setIsLikesPostOpen(false)}
            >
                <LikesModal
                    likes={info.likes}
                    onClose={() => setIsLikesPostOpen(false)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isAttendeesPostOpen}
                handleClose={() => setIsAttendeesPostOpen(false)}
            >
                <UserListModal
                    attendees={info.attendees}
                    onClose={() => setIsAttendeesPostOpen(false)}
                    title="Asistentes"
                />
            </CustomModal>
            {info.likes.length > 0 && (
                <Tooltip
                    anchorSelect={`.tooltip-${info.id}`}
                    place="right"
                    className="shadow-custom bg-secondary text-white rounded-md"
                >
                    <LikesTooltip likes={info.likes} />
                </Tooltip>
            )}
        </>
    )
}

export default Post
